import { useParams, Link } from 'react-router-dom';
import { useEffect, useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEOHead from '@/components/feature/SEOHead';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { categories } from '@/mocks/categories';
import { deals, type Deal } from '@/mocks/deals';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [filteredDeals, setFilteredDeals] = useState<Deal[]>([]);
  const [sortBy, setSortBy] = useState<'default' | 'price-asc' | 'price-desc'>('default');

  const currentCategory = categories.find(c => c.slug === slug) || categories[0];
  const categoryName = currentCategory?.name || '全部福利';

  useEffect(() => {
    let result = slug && slug !== 'all'
      ? deals.filter(d => d.category === slug)
      : [...deals];

    if (sortBy === 'price-asc') {
      result.sort((a, b) => a.currentPrice - b.currentPrice);
    } else if (sortBy === 'price-desc') {
      result.sort((a, b) => b.currentPrice - a.currentPrice);
    }

    setFilteredDeals(result);
  }, [slug, sortBy]);

  return (
    <>
      <ScrollToTop />
      <SEOHead
        title={`${categoryName} - 福利吧`}
        description={`福利吧${categoryName}频道，每日精选${categoryName}福利信息，限时折扣、免费领取、优惠券一网打尽，帮你省钱又省心。`}
        keywords={`${categoryName},福利吧,每日福利,优惠活动,省钱`}
        canonicalUrl={`https://fuliba.com/category/${slug}`}
        breadcrumbs={[
          { name: '首页', url: 'https://fuliba.com/' },
          { name: categoryName, url: `https://fuliba.com/category/${slug}` },
        ]}
      />

      <Navbar />

      <main className="pt-20 pb-14 min-h-screen bg-background-50">
        <div className="w-full px-4 md:px-6">
          <nav className="flex items-center gap-2 text-sm text-foreground-500 mb-6 flex-wrap">
            <Link to="/" className="hover:text-primary-600 transition-colors cursor-pointer">首页</Link>
            <i className="ri-arrow-right-s-line text-xs"></i>
            <span className="text-foreground-900 font-medium">{categoryName}</span>
          </nav>

          <div className="mb-8">
            <h1 className="text-2xl md:text-3xl font-bold font-heading text-foreground-950 flex items-center gap-2">
              <i className={`${currentCategory?.icon || 'ri-star-line'} text-primary-500`}></i>
              {categoryName}
            </h1>
            <p className="text-foreground-600 text-sm mt-2">
              共 {filteredDeals.length} 个福利 · 每日更新
            </p>
          </div>

          <div className="flex flex-col lg:flex-row gap-8">
            <aside className="lg:w-56 flex-shrink-0">
              <div className="sticky top-24">
                <h4 className="font-bold text-foreground-900 text-sm mb-3">福利分类</h4>
                <nav className="space-y-1">
                  <Link
                    to="/category/all"
                    className={`block px-3 py-2 rounded-lg text-sm transition-colors cursor-pointer ${
                      slug === 'all' || !slug
                        ? 'bg-primary-100 text-primary-700 font-medium'
                        : 'text-foreground-600 hover:bg-background-100 hover:text-foreground-900'
                    }`}
                  >
                    全部福利
                  </Link>
                  {categories.filter(c => c.slug !== 'all').map((cat) => (
                    <Link
                      key={cat.slug}
                      to={`/category/${cat.slug}`}
                      className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors cursor-pointer ${
                        slug === cat.slug
                          ? 'bg-primary-100 text-primary-700 font-medium'
                          : 'text-foreground-600 hover:bg-background-100 hover:text-foreground-900'
                      }`}
                    >
                      <i className={`${cat.icon} text-base`}></i>
                      {cat.name}
                    </Link>
                  ))}
                </nav>

                <h4 className="font-bold text-foreground-900 text-sm mt-8 mb-3">排序方式</h4>
                <div className="space-y-1">
                  {(['default', 'price-asc', 'price-desc'] as const).map((s) => (
                    <button
                      key={s}
                      onClick={() => setSortBy(s)}
                      className={`block w-full text-left px-3 py-2 rounded-lg text-sm transition-colors cursor-pointer whitespace-nowrap ${
                        sortBy === s
                          ? 'bg-accent-100 text-accent-700 font-medium'
                          : 'text-foreground-600 hover:bg-background-100 hover:text-foreground-900'
                      }`}
                    >
                      {s === 'default' && '默认排序'}
                      {s === 'price-asc' && '价格从低到高'}
                      {s === 'price-desc' && '价格从高到低'}
                    </button>
                  ))}
                </div>
              </div>
            </aside>

            <div className="flex-1">
              {filteredDeals.length === 0 ? (
                <div className="text-center py-20">
                  <div className="w-16 h-16 mx-auto mb-4 bg-background-100 rounded-full flex items-center justify-center">
                    <i className="ri-inbox-line text-2xl text-foreground-400"></i>
                  </div>
                  <p className="text-foreground-500 text-sm">暂无相关福利，请查看其他分类</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
                  {filteredDeals.map((deal) => (
                    <article
                      key={deal.id}
                      className="bg-background-50 border border-background-200/70 rounded-xl overflow-hidden hover:border-primary-200 transition-all group cursor-pointer"
                    >
                      <div className="relative h-44 overflow-hidden">
                        <img
                          src={deal.image}
                          alt={deal.title}
                          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                        />
                        <div className="absolute top-2 left-2 flex flex-wrap gap-1.5">
                          <span className="px-2 py-0.5 rounded-full bg-primary-500 text-background-50 text-xs font-bold">
                            {deal.discount}
                          </span>
                          {deal.isFree && (
                            <span className="px-2 py-0.5 rounded-full bg-accent-500 text-background-50 text-xs font-bold">
                              免费
                            </span>
                          )}
                          {deal.isNew && (
                            <span className="px-2 py-0.5 rounded-full bg-secondary-500 text-background-50 text-xs font-bold">
                              NEW
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="p-3">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-foreground-500 text-xs">{deal.source}</span>
                          <span className="text-foreground-300 text-xs">·</span>
                          <span className="text-foreground-500 text-xs">截止 {deal.expiryDate}</span>
                        </div>
                        <h3 className="font-semibold text-foreground-900 text-sm line-clamp-2 group-hover:text-primary-600 transition-colors">
                          {deal.title}
                        </h3>
                        <p className="text-foreground-600 text-xs mt-1 line-clamp-2">{deal.description}</p>
                        <div className="flex items-center justify-between mt-3">
                          <div className="flex items-baseline gap-1.5">
                            <span className="text-lg font-bold text-primary-600">
                              {deal.currentPrice === 0 ? '免费' : `¥${deal.currentPrice}`}
                            </span>
                            {deal.originalPrice > 0 && (
                              <span className="text-foreground-400 text-xs line-through">¥{deal.originalPrice}</span>
                            )}
                          </div>
                          <a
                            href={deal.link}
                            className="px-3 py-1.5 bg-primary-500 hover:bg-primary-600 text-background-50 text-xs font-medium rounded-full transition-colors whitespace-nowrap cursor-pointer"
                            rel="nofollow"
                          >
                            立即前往
                          </a>
                        </div>
                      </div>
                    </article>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </>
  );
}